export default {
  app: {
    name: 'NomadMate',
  },
  notFound: {
    title: 'Page Not Found',
    description: 'The page you are looking for does not exist',
    goBack: 'Go Home',
  },
  common: {
    loading: 'Loading...',
    noData: 'No data available',
    error: 'Something went wrong, please retry',
    retry: 'Retry',
    authExpired: 'Session expired, please log in again',
    requestFailed: 'Request failed',
    networkError: 'Network error, please check your connection',
    skipToContent: 'Skip to main content',
  },
  nav: {
    dashboard: 'Dashboard',
    visa: 'Visa',
    cost: 'Cost',
    timezone: 'Timezone',
    tax: 'Tax',
    guide: 'Guide',
    login: 'Login',
  },
  footer: {
    about: 'About',
    privacy: 'Privacy',
    terms: 'Terms',
    copyright: '© 2026 NomadMate',
  },
  index: {
    hero: {
      title: 'Your Digital Nomad Life, Organized',
      subtitle: 'All-in-one visa tracking, cost comparison, timezone coordination, tax compliance, and city guide platform',
      cta: 'Get Started Free',
      learnMore: 'Learn More',
    },
    features: {
      title: 'Core Features',
      visa: { title: 'Visa Tracker', desc: 'Track visa types, validity periods, and renewal reminders across countries' },
      cost: { title: 'Cost Compare', desc: 'Compare living costs including accommodation, food, transport and more' },
      timezone: { title: 'Timezone Manager', desc: 'Multi-timezone clocks and cross-timezone meeting coordination' },
      tax: { title: 'Tax Assistant', desc: 'Cross-border tax rules lookup and residence day risk alerts' },
      guide: { title: 'City Guide', desc: 'Detailed guides for digital nomad destinations worldwide' },
    },
    stats: {
      countries: 'Countries',
      cities: 'Cities',
      languages: 'Languages',
    },
  },
  login: {
    login: 'Login',
    register: 'Register',
    subtitle: 'Log in to NomadMate and manage your nomad life',
    registerSubtitle: 'Create an account to start your digital nomad journey',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    emailInvalid: 'Please enter a valid email address',
    username: 'Username',
    usernamePlaceholder: 'Enter username',
    password: 'Password',
    passwordPlaceholder: '••••••••',
    passwordTooShort: 'Password must be at least 6 characters',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Re-enter password',
    passwordMismatch: 'Passwords do not match',
    loading: 'Logging in...',
    error: 'Login failed, please try again',
    orContinueWith: 'Or continue with',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
  },
  dashboard: {
    welcome: 'Welcome back, {name}',
    subtitle: 'Your nomad life overview',
    stats: {
      activeVisas: 'Active Visas',
      daysUntilExpiry: 'Days Until Expiry',
      citiesCompared: 'Cities Compared',
      timezones: 'Tracked Timezones',
    },
    expiryAlerts: 'Visa Expiry Alerts',
    noAlerts: 'No upcoming expiry alerts',
    daysLeft: '{days} days left',
    worldClocks: 'World Clocks',
    quickActions: 'Quick Actions',
    actions: {
      visa: 'Visa Tracker',
      cost: 'Cost Compare',
      timezone: 'Timezone',
      tax: 'Tax Assistant',
      guide: 'City Guide',
    },
    recentActivity: 'Recent Activity',
    noActivity: 'No recent activity',
  },
  visa: {
    title: 'Visa Tracker',
    subtitle: 'Track and manage your visa information',
    searchPlaceholder: 'Search by country name...',
    allRegions: 'All Regions',
    regions: {
      europe: 'Europe',
      asia: 'Asia',
      americas: 'Americas',
      oceania: 'Oceania',
    },
    noCountries: 'No countries found matching your criteria',
    requirements: 'Requirements',
    documents: 'Required Documents',
    track: 'Track This Visa',
    applyUrl: 'Apply Link',
    myTracked: 'My Tracked Visas',
  },
  cost: {
    title: 'Cost Comparison',
    subtitle: 'Compare living costs across different cities',
    selectCities: 'Select Cities',
    selectHint: 'Select 2-4 cities to compare',
    compare: 'Compare',
    selectedCount: '{count} cities selected',
    comparison: 'Comparison',
    category: 'Category',
    nomadScore: 'Nomad Score',
    budgetFinder: 'Budget Finder',
    budgetPlaceholder: 'Enter monthly budget',
    findCities: 'Find Cities',
    aiSuggestion: 'AI suggestions coming soon',
  },
  timezone: {
    title: 'Timezone Manager',
    subtitle: 'Manage multi-timezone schedules and meetings',
    addTimezone: 'Add Timezone',
    noTimezones: 'Click buttons above to add timezones',
    meetingScheduler: 'Meeting Scheduler',
    meetingHint: 'Select participant timezones to find the best meeting time',
    findMeeting: 'Find Meeting Time',
    finding: 'Finding...',
    bestTime: 'Best Time',
    workingOverlap: 'Working Hours Overlap',
  },
  tax: {
    title: 'Tax Assistant',
    subtitle: 'Cross-border tax calculation and compliance',
    rules: 'Tax Rules',
    selectCountry: 'Select Country',
    thresholdDays: 'Threshold Days',
    days: 'days',
    taxRates: 'Tax Rates',
    reportingRequirements: 'Reporting Requirements',
    importantDates: 'Important Dates',
    residenceTracker: 'Residence Day Tracker',
    noResidenceDays: 'No residence days recorded',
    riskIndicator: 'Tax Risk Indicator',
    noRiskData: 'No risk data available',
    countries: {
      portugal: 'Portugal',
      thailand: 'Thailand',
      spain: 'Spain',
      germany: 'Germany',
      estonia: 'Estonia',
      georgia: 'Georgia',
      croatia: 'Croatia',
      mexico: 'Mexico',
      colombia: 'Colombia',
      indonesia: 'Indonesia',
      japan: 'Japan',
      southKorea: 'South Korea',
      unitedKingdom: 'United Kingdom',
      unitedStates: 'United States',
    },
  },
  guide: {
    title: 'City Guide',
    subtitle: 'Global digital nomad destination guides',
    allContinents: 'All',
    noCities: 'No cities found matching your criteria',
    backToList: 'Back to list',
    internetQuality: 'Internet Quality',
    safetyRating: 'Safety Rating',
    climate: 'Climate',
    communitySize: 'Community Size',
    coworkingSpaces: 'Coworking Spaces',
    noCoworking: 'No coworking spaces available',
    day: 'day',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your account and app preferences',
    profile: 'Profile',
    username: 'Username',
    email: 'Email',
    saveProfile: 'Save Profile',
    saving: 'Saving...',
    language: 'Language',
    notifications: 'Notifications',
    visaExpiryNotif: 'Visa Expiry Reminders',
    taxReminderNotif: 'Tax Reminders',
    appearance: 'Appearance',
    light: 'Light',
    dark: 'Dark',
    baseTimezone: 'Base Timezone',
    dangerZone: 'Danger Zone',
    dangerDesc: 'These actions are irreversible, proceed with caution',
    deleteAccount: 'Delete Account',
    confirmDelete: 'Confirm Account Deletion',
    confirmDeleteDesc: 'This will permanently delete your account and all data. This action cannot be undone.',
    cancel: 'Cancel',
    confirm: 'Delete',
  },
  about: {
    title: 'About',
    subtitle: 'NomadMate - Digital Nomad Life Assistant',
    version: 'Version',
    versionNumber: '1.0.0',
    description: 'NomadMate is a life assistant designed for digital nomads, providing visa management, cost of living comparison, timezone management, tax calculation, and city guides to help you easily manage your global nomad life.',
    openSource: 'Open Source Acknowledgements',
    openSourceDesc: 'This project is built on the following excellent open source projects',
    contact: 'Contact Us',
    support: 'Support',
    feedback: 'Feedback',
    privacyPolicy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
}
